import { messages as pasteMessages } from '@databases/copy-paste/messages';
import { pasteContentExperience } from '@databases/frontend-metrics';
import { showErrorSnackbar } from '@databases/utils/errors';
import { useCallback } from 'react';
import { useIntl } from 'react-intl-next';

interface UseAttachmentPasteOptions {
  /**
   * Whether the current value is the only value selected
   */
  valueIsTheOnlyValueSelected: boolean;
  /**
   * Callback to handle dropped files
   */
  handleDroppedFiles: (files: File[]) => void;
  /**
   * Additional files to include when handling paste (used in some components)
   */
  acceptedFiles?: File[];
}

export const useAttachmentPaste = ({
  valueIsTheOnlyValueSelected,
  handleDroppedFiles,
  acceptedFiles = [],
}: UseAttachmentPasteOptions) => {
  const { formatMessage } = useIntl();

  const handlePaste = useCallback(
    (items: DataTransferItemList) => {
      if (!valueIsTheOnlyValueSelected) {
        return;
      }

      if (items.length === 0) {
        return;
      }

      const imageItems = Array.from(items).filter((item) =>
        item.type.startsWith('image/')
      );

      if (imageItems.length === 0) {
        return;
      }

      try {
        pasteContentExperience.start();

        const firstImageFile = imageItems[0]?.getAsFile();
        if (firstImageFile) {
          // Include acceptedFiles if provided (used in AttachmentValueEdit)
          const filesToProcess =
            acceptedFiles.length > 0
              ? [...acceptedFiles, firstImageFile]
              : [firstImageFile];

          handleDroppedFiles(filesToProcess);
          pasteContentExperience.success();
          return;
        }
      } catch (e) {
        showErrorSnackbar(
          'cannot-read-pasted-content',
          formatMessage(pasteMessages['flag-error-message.cannot-read-pasted-content'])
        );
        pasteContentExperience.failure();
        return;
      }
    },
    [valueIsTheOnlyValueSelected, formatMessage, handleDroppedFiles, acceptedFiles]
  );

  return { handlePaste };
};
